import React from "react";

import RevertIcon from "@uui/ClockRewind";
import MenuIcon from "@uui/DotsHorizontal";

import {
  FloatingNavActionDivider,
  FloatingNavIconButton,
} from "@app/analytics/components/reports/FloatingNav";
import useEditReport from "@app/analytics/MeetingReport/hooks/useEditReport";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import type { RecapMainSection } from "@app/analytics/MeetingReport/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Column } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

export const RestoreMenuItems = ({
  onClick,
  section,
}: {
  onClick: () => void;
  section?: RecapMainSection;
}) => {
  const { canRestoreNotes, canRestoreTranscript } = useEditReport();
  const setModal = useEditStore((state) => state.setModal);

  if (
    (section === "notes" && !canRestoreNotes) ||
    (section === "transcript" && !canRestoreTranscript)
  ) {
    return null;
  }

  const handleClickRevertNotes = () => {
    setModal("confirm_revert_notes");
    onClick();
  };

  const handleClickRevertTranscript = () => {
    setModal("confirm_revert_transcript");
    onClick();
  };

  const showRevertNotes = (section ?? "notes") === "notes";
  const showRevertTranscript = (section ?? "transcript") === "transcript";

  return (
    <Column display="contents">
      {showRevertNotes && (
        <TooltipWrapper
          title="The notes have not been edited"
          show={!canRestoreNotes}
          enterDelay={2000}
        >
          <MenuItem
            icon={section ? <RevertIcon /> : undefined}
            disabled={!canRestoreNotes}
            onClick={handleClickRevertNotes}
          >
            Revert notes to original
          </MenuItem>
        </TooltipWrapper>
      )}
      {showRevertTranscript && (
        <TooltipWrapper
          title="The transcript has not been edited"
          show={!canRestoreTranscript}
          enterDelay={2000}
        >
          <MenuItem
            icon={section ? <RevertIcon /> : undefined}
            disabled={!canRestoreTranscript}
            onClick={handleClickRevertTranscript}
          >
            Revert transcript to original
          </MenuItem>
        </TooltipWrapper>
      )}
    </Column>
  );
};

const RestoreMenu = () => {
  const { open, onClose, onClick, anchorEl } = useAnchorElement();

  return (
    <>
      <FloatingNavActionDivider />
      <FloatingNavIconButton Icon={MenuIcon} onClick={onClick} />
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <RestoreMenuItems onClick={onClose} />
      </Menu>
    </>
  );
};

export default RestoreMenu;
